/**
 * The page speed card on a Saidiseire site page: the CRM's side of the
 * contract, and the rules the card needs (docs/saidiseire-kiirus-turve-raport.md,
 * item C).
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-monitoring/speed/types.ts`.
 * `monitoringSpeed.test.ts` reads that file and compares the field names of
 * every interface, holds `SPEED_ERROR_CODES` to the API's `SpeedError` union
 * and `SPEED_LIMITS` to its `SpeedLimit`, and holds the polling numbers to
 * `periodic/claim.ts`.
 *
 * The PageSpeed key has a call budget (25 000 a day, 750 000 a month by
 * default, set in Settings > Integrations > PageSpeed). While it is used up,
 * the API says `budgetSpent`, whenever the site is next due: the card then
 * says so calmly instead of showing an error, and offers no run now, which
 * the API would refuse with `budget_reached`. `nextDueAt` already carries the
 * moment measuring can resume. Google's per-minute limit (`rate_limited`)
 * spends no budget: it is the newest run's failure like any other, and the
 * next wake goes on.
 *
 * Page speed is a different number from the server response time the uptime
 * checks record (`ResponseChart`): this is how the whole page loads in a
 * browser, measured by Google's Lighthouse once a week.
 *
 * React-free: which colour a score wears, how a metric reads, which state the
 * card is in and when it stops polling are decided here and tested without
 * mounting anything.
 */
import { api } from "./api";
import { MONITOR_SITES_PATH, formatMs } from "./monitoring";

// ── Contract (mirrors crm-monitoring/speed/types.ts) ────────────────────────────

export type SpeedStrategy = "mobile" | "desktop";

/** Which part of the PageSpeed call budget is used up: today's (Pacific day) or this month's. */
export type SpeedLimit = "day" | "month";

export type SpeedError =
  | "no_key"
  | "bad_key"
  | "quota"
  | "rate_limited"
  | "unreachable"
  | "lighthouse_error"
  | "timeout"
  | "bad_answer";

export interface SpeedMetricsView {
  fcpMs: number | null;
  lcpMs: number | null;
  tbtMs: number | null;
  cls: number | null;
  siMs: number | null;
  ttfbMs: number | null;
}

/** Each metric's own 0..1 Lighthouse audit score, or null. */
export interface SpeedMetricScoresView {
  fcp: number | null;
  lcp: number | null;
  tbt: number | null;
  cls: number | null;
  si: number | null;
  ttfb: number | null;
}

export interface SpeedRunView {
  at: string;
  strategy: SpeedStrategy;
  score: number;
  metrics: SpeedMetricsView;
  metricScores: SpeedMetricScoresView;
  lighthouseVersion: string | null;
}

export interface SpeedLatestView {
  mobile: SpeedRunView | null;
  desktop: SpeedRunView | null;
}

export interface SpeedHistoryPoint {
  at: string;
  strategy: SpeedStrategy;
  score: number;
}

export interface SpeedLastError {
  at: string;
  /** The strategy that failed (mobile first), or `both` when both failed with the same error. */
  strategy: SpeedStrategy | "both";
  error: string;
}

export interface SpeedCardResponse {
  allowed: boolean;
  keySet: boolean;
  running: boolean;
  latest: SpeedLatestView;
  /** Oldest first. */
  history: SpeedHistoryPoint[];
  lastError: SpeedLastError | null;
  /** Never earlier than the call budget lets a site run again. */
  nextDueAt: string | null;
  /** The part of the call budget used up right now (a run now would be refused), or null; null too while the site cannot run. */
  budgetSpent: SpeedLimit | null;
}

export interface SpeedRunStarted {
  startedAt: string;
}

// ── Routes ───────────────────────────────────────────────────────────────────

export const speedPath = (monitorId: string) => `${MONITOR_SITES_PATH}/${encodeURIComponent(monitorId)}/speed`;

export const getSpeed = (monitorId: string, signal?: AbortSignal): Promise<SpeedCardResponse> =>
  api.get<SpeedCardResponse>(speedPath(monitorId), signal);

/** A run on request. Refusals are read by `monitorErrorKey` (they are the manual run's shared ones). */
export const runSpeedNow = (monitorId: string): Promise<SpeedRunStarted> => api.post<SpeedRunStarted>(`${speedPath(monitorId)}/run`);

// ── Polling ──────────────────────────────────────────────────────────────────

/** How often the card asks while a run is going on (`RUN_POLL_MS` in periodic/claim.ts). */
export const SPEED_POLL_MS = 5_000;

/** How long one run may take (`RUN_MAX_MS.speed` in periodic/claim.ts): two PageSpeed calls and storing. */
export const SPEED_RUN_MAX_MS = 240_000;

/** The card stops asking this long after it started, and says the run is still going. */
export const SPEED_POLL_LIMIT_MS = SPEED_RUN_MAX_MS + 30_000;

/**
 * What the card's poll does now: nothing (no run), ask again, or give up and
 * say "still running". `since` is when this card first saw the run, in ms.
 */
export function pollStep(running: boolean, since: number | null, now: number): "idle" | "poll" | "gaveUp" {
  if (!running) return "idle";
  if (since !== null && now - since >= SPEED_POLL_LIMIT_MS) return "gaveUp";
  return "poll";
}

/**
 * What a FRESH answer does to the poll: no run closes the window; a run seen
 * for the first time opens it at `now`; a run still going past the window
 * gives up ("still running"). Only an answer read just now may give up: a tab
 * hidden past the window reads once more when it comes back, and shows a run
 * that finished meanwhile instead of a stale "still running".
 */
export function pollAfterAnswer(running: boolean, since: number | null, now: number): { since: number | null; gaveUp: boolean } {
  if (!running) return { since: null, gaveUp: false };
  if (since === null) return { since: now, gaveUp: false };
  return { since, gaveUp: pollStep(true, since, now) === "gaveUp" };
}

// ── Rules ────────────────────────────────────────────────────────────────────

export type SpeedBand = "good" | "average" | "poor";

/** Lighthouse's bands for the performance score: 90-100 good, 50-89 needs work, 0-49 poor. */
export function scoreBand(score: number): SpeedBand {
  if (score >= 90) return "good";
  if (score >= 50) return "average";
  return "poor";
}

/**
 * A metric's band from its own 0..1 audit score (at least 0.9 good, 0.5 to
 * 0.89 needs work, below 0.5 poor). Lighthouse scores each metric against the
 * thresholds of the strategy it ran with, so this is right for desktop too,
 * where fixed mobile thresholds would colour everything green. Null: no score.
 */
export function metricBand(score: number | null): SpeedBand | null {
  if (score === null || !Number.isFinite(score)) return null;
  if (score >= 0.9) return "good";
  if (score >= 0.5) return "average";
  return "poor";
}

/** A band as a status pill's tone; no band is muted. */
export function bandTone(band: SpeedBand | null): "ok" | "wt" | "lt" | "mut" {
  if (band === "good") return "ok";
  if (band === "average") return "wt";
  if (band === "poor") return "lt";
  return "mut";
}

/** A band as a text colour. */
export function bandColor(band: SpeedBand | null): string {
  if (band === "good") return "var(--color-success)";
  if (band === "average") return "var(--color-warning)";
  if (band === "poor") return "var(--color-destructive)";
  return "var(--color-muted-foreground)";
}

export type SpeedMetricKey = keyof SpeedMetricScoresView;

/** The six metrics in the order Lighthouse lists them, TTFB last. */
export const SPEED_METRICS: readonly SpeedMetricKey[] = ["fcp", "lcp", "tbt", "cls", "si", "ttfb"];

const METRIC_VALUE: Readonly<Record<SpeedMetricKey, keyof SpeedMetricsView>> = {
  fcp: "fcpMs",
  lcp: "lcpMs",
  tbt: "tbtMs",
  cls: "cls",
  si: "siMs",
  ttfb: "ttfbMs",
};

export function metricValue(metrics: SpeedMetricsView, key: SpeedMetricKey): number | null {
  return metrics[METRIC_VALUE[key]];
}

/**
 * A metric as it reads on screen: times like the rest of the section
 * (`formatMs`: "320 ms", "2,1 s"), layout shift as a plain number with three
 * decimals, "-" when there is none. Estonian number shapes, like every CRM figure.
 */
export function formatMetric(key: SpeedMetricKey, value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "-";
  if (key === "cls") return value.toLocaleString("et-EE", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  return formatMs(value);
}

/** Every code the API stores for a failed run (held to the API's `SpeedError` by the test). */
export const SPEED_ERROR_CODES: readonly SpeedError[] = [
  "no_key",
  "bad_key",
  "quota",
  "rate_limited",
  "unreachable",
  "lighthouse_error",
  "timeout",
  "bad_answer",
];

/** The code as a key under `monitoring.speed.error`, or null for one this browser does not know (shown as it came). */
export function speedErrorKey(code: string): SpeedError | null {
  return (SPEED_ERROR_CODES as readonly string[]).includes(code) ? (code as SpeedError) : null;
}

/**
 * The sentence of the newest run's failure: one strategy named in brackets, or
 * one that says both failed (the API reports `both` when mobile and desktop
 * failed with the same error, rather than naming mobile alone).
 */
export function lastErrorKey(e: Pick<SpeedLastError, "strategy">): "monitoring.speed.lastError" | "monitoring.speed.lastErrorBoth" {
  return e.strategy === "both" ? "monitoring.speed.lastErrorBoth" : "monitoring.speed.lastError";
}

/**
 * Which story the card tells first: a run going on; the package leaves the
 * test out; no key (nothing is measured); never measured; or results.
 */
export type SpeedCardState = "running" | "notInPlan" | "noKey" | "never" | "measured";

export function speedCardState(data: SpeedCardResponse): SpeedCardState {
  if (data.running) return "running";
  if (!data.allowed) return "notInPlan";
  if (!data.keySet) return "noKey";
  if (!data.latest.mobile && !data.latest.desktop) return "never";
  return "measured";
}

/**
 * A run on request makes sense: in the package, a key stored, nothing going
 * on, and the call budget not spent. The budget counts whenever the site is
 * next due: a run on request does not wait for the weekly rule, and the API
 * refuses it (`budget_reached`) while fewer than two calls are left.
 */
export function canRunNow(data: SpeedCardResponse): boolean {
  return data.allowed && data.keySet && !data.running && data.budgetSpent === null;
}

/** Every part of the budget the API can name (held to the API's `SpeedLimit` by the test). */
export const SPEED_LIMITS: readonly SpeedLimit[] = ["day", "month"];

/** The calm line of a spent budget, under `monitoring.speed.limit`. */
export function limitKey(limit: SpeedLimit): "monitoring.speed.limit.day" | "monitoring.speed.limit.month" {
  return limit === "month" ? "monitoring.speed.limit.month" : "monitoring.speed.limit.day";
}

/**
 * The newest run's failure as the card shows it: none while a run is going
 * on, and none when it is Google's 429 (`quota`) while the budget is spent,
 * because the budget line tells that story calmly. After a 429 the API marks
 * the day spent, so the two come together.
 */
export function shownLastError(data: SpeedCardResponse): SpeedLastError | null {
  if (!data.lastError || data.running) return null;
  if (data.budgetSpent && data.lastError.error === "quota") return null;
  return data.lastError;
}

/** The mobile score's trend for the chart, oldest first, as epoch ms. */
export function mobileTrend(history: readonly SpeedHistoryPoint[]): { t: number; score: number }[] {
  return history
    .filter((p) => p.strategy === "mobile")
    .map((p) => ({ t: Date.parse(p.at), score: p.score }))
    .filter((p) => Number.isFinite(p.t))
    .sort((a, b) => a.t - b.t);
}

/** The newest run's moment across both strategies, for the "measured" line. */
export function lastMeasuredAt(latest: SpeedLatestView): string | null {
  const times = [latest.mobile?.at, latest.desktop?.at].filter((x): x is string => Boolean(x)).sort();
  return times[times.length - 1] ?? null;
}
